import type { EntityState } from '@reduxjs/toolkit';

import type { reportConfigurationsTypes } from '../report-configurations';
import type { TransactionRecord } from '../transactions/transactionsTypes';

export const GENERATED_REPORTS_REDUCER_KEY = 'generatedReportsStore';
export type GENERATED_REPORTS_REDUCER_KEY = typeof GENERATED_REPORTS_REDUCER_KEY;
/** Bump when the cached report shape or allowed report names change. */
export const GENERATED_REPORTS_VERSION_NUMBER = 2;

/**
 * Store locations for the web dashboard's fixed report cards. Each card keeps its own copy, separate from
 * Reports-page instances of the same predefined configuration, and these slots are cached across reloads.
 */
export const DASHBOARD_REPORT_LOCATIONS = {
  income: 'dashboard-income',
  spending: 'dashboard-spending',
  profitLoss: 'dashboard-profit-loss',
} as const;

// columns can be by account, date-range, payee, tag, entity (e.g. business, client, project)
export type CellType = 'AMOUNT' | 'DATE' | 'PERCENTAGE' | 'QCS_ENTITY' | 'STRING';

export type RowLabels = {
  positiveLabel: string;
  negativeLabel: string;
  combinedLabel?: string;
};

/**
 * A comparison column either carries a range's raw value or a value derived from it.
 * `VALUE` columns are the only drill-downable ones — see `comparisonColumnModel`.
 */
export type ComparisonColumnType = 'VALUE' | reportConfigurationsTypes.AdditionalComparisonColumnType;

/** Comparison columns are grouped into aligned periods plus one trailing whole-range total. */
export type ComparisonColumnGroupType = 'PERIOD' | 'TOTAL';

export type ReportColumn = {
  id?: string;

  cellType?: CellType;

  isAggSum?: boolean;

  // --- COMPARISON reports only; absent on SUMMARY and TRANSACTION ---
  /** Groups columns into an aligned period (or the total). Group by this, never by the label. */
  columnGroupId?: string;
  /** Server-supplied English display string ("Period 1", "Total"). Display only — never a key. */
  columnGroupLabel?: string;
  columnGroupType?: ComparisonColumnGroupType;
  /** `'PRIMARY'` for the primary range, otherwise a `ComparisonRange['rangeId']`. */
  rangeId?: string;
  /** Server-supplied English label for the range ("Jan 2024"). Display only — never a key. */
  rangeLabel?: string;
  comparisonColumnType?: ComparisonColumnType;
} & (
  | {
      headerType: 'STRING';
      headerValue: {
        id: string;
        string: string;
      };
    }
  | {
      headerType: 'DATE_RANGE';
      headerValue: {
        startOn: string; // ISO Date
        endOn: string;
        /** Display name for the period ("Jan 2025"). COMPARISON reports only. */
        name?: string;
      };
    }
  | {
      headerType: 'QCS_ENTITY';
      headerValue: {
        qcs_entity: 'PAYEE' | 'TAG' | 'ACCOUNT' | 'BUSINESS';
        id: string;
        name?: string;
      };
    }
);

export type CellItem = {
  amount: number;
  amountOn: string;
  txnId?: string;
  splitItemId?: string;
  paymentTxnId?: string;
  featurePath?: string;
  vehicleId?: string;
  secondaryText?: string;
};

export type ReportCell = {
  id?: string;
  columnId?: string;
  value?: string;
  startValue?: string;
  items?: CellItem[];
  itemCount?: number; // compact count retained in case items are pruned

  // --- COMPARISON reports only ---
  // NOTE: `ReportRow` below already has `deltaAmount`/`deltaPercentage`. Those are ROW-level and
  // are used by NetWorth (earliest vs latest balance). These are CELL-level and mean this range's
  // value versus the primary range's value for the same period. Same names, different semantics —
  // do not conflate them.
  deltaAmount?: string | null;
  /** `null` is meaningful and distinct from absent: the comparison value was zero, so no ratio. */
  deltaPercentage?: string | null;
};

export type NetWorthRow = {
  id?: string;
  name?: string;
  entityId?: string;
  isClosed?: boolean;
  earliestValue: number | null;
  latestValue: number | null;
  deltaAmount?: number | null;
  deltaPercentage?: number | null;
  isExcludedFromReports?: boolean;
  isTopLevel?: boolean;
  includedCount?: number | undefined;
  excludedCount?: number | undefined;
  children?: NetWorthRow[];
};

export type ReportRow = {
  id?: string;
  name?: string;
  secondaryText?: string;
  featurePath?: string;
  parentId?: string;
  entityId?: string;
  entityType?: string;
  childrenEntityIds?: string[];
  isExcludedFromReports?: boolean;
  expectedSign?: 'POSITIVE' | 'NEGATIVE';
  cellData: ReportCell[];
  other?: boolean;
  isTotal?: boolean;
  deltaAmount?: string | null;
  deltaPercentage?: string | null;
};

export type Report = {
  configuration: reportConfigurationsTypes.ReportConfiguration;
  name: string;

  reportType: reportConfigurationsTypes.ReportType;
  reportSubType?: reportConfigurationsTypes.ReportSubType;
  persistentType?: reportConfigurationsTypes.PersistentType;

  // ISO Dates
  startOn: string;
  endOn: string;

  /**
   * Whole-report scalar total. For COMPARISON this reflects the PRIMARY range only and is
   * ambiguous across multiple ranges — read the selected comparison column group instead.
   */
  total?: number;

  rowType?: reportConfigurationsTypes.RowType;
  columnType?: reportConfigurationsTypes.ColumnType;

  rows: ReportRow[];
  columns: ReportColumn[];
};

export type Breadcrumb = {
  category: string;
  categoryId?: string;
  entityId?: string;
  name?: string;
  id?: string;
};

export type TransformResponse = {
  data: {
    resources?: Array<{
      reportType: reportConfigurationsTypes.ReportType;
      reportSubType?: reportConfigurationsTypes.ReportSubType;
      persistentType?: reportConfigurationsTypes.PersistentType;
      startOn: string;
      endOn: string;
      total?: number;
      rowType?: reportConfigurationsTypes.RowType;
      columnType?: reportConfigurationsTypes.ColumnType;
      rows: ReportRow[];
      columns: ReportColumn[];
    }>;
    reportType?: reportConfigurationsTypes.ReportType;
    reportSubType?: reportConfigurationsTypes.ReportSubType;
    persistentType?: reportConfigurationsTypes.PersistentType;
    startOn?: string;
    endOn?: string;
    total?: number;
    rowType?: reportConfigurationsTypes.RowType;
    columnType?: reportConfigurationsTypes.ColumnType;
    rows?: ReportRow[];
    columns?: ReportColumn[];
  };
};

export type ReportTransactionReconciliationOutcomeStatus = 'found' | 'absent' | 'failed';

export type ReportTransactionReconciliationOutcome = {
  id: string;
  status: ReportTransactionReconciliationOutcomeStatus;
};

export type ReportTransactionReconciliation = {
  generation: number;
  status: 'loading' | 'complete' | 'partial' | 'failed';
  requestedIds: string[];
  unresolvedIds: string[];
  outcomes: ReportTransactionReconciliationOutcome[];
  error?: string;
};

export type ReconcileReportTransactionsPayload = {
  location: string;
  generation: number;
  attemptKey: string;
  transactionIds: string[];
};

export type ReconcileReportTransactionsSuccessPayload = ReconcileReportTransactionsPayload & {
  resources: TransactionRecord[];
  outcomes: ReportTransactionReconciliationOutcome[];
};

export type EntityValue = {
  location: string;
  isGenerating: boolean;

  report?: Report | null;
  error?: Error | string | null;
  generatedAtVersion?: number;
  generation?: number;
  transactionReconciliations?: Record<string, ReportTransactionReconciliation>;
};

export type GeneratedReportsState = EntityState<EntityValue, string> & {
  activeViewingConfigId: string | null;
  dataVersion: number;
};

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [GENERATED_REPORTS_REDUCER_KEY]: GeneratedReportsState;
  }
}
